import { useState, type FormEvent } from 'react';
import { createWebEvent, type CreateWebEventInput } from '../services/portal';
import type { WebEvent } from '../types/events';

function toLocalInput(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

type CreateEventPanelProps = {
  userId: string;
  onClose: () => void;
  onCreated: (event: WebEvent) => void;
};

export function CreateEventPanel({ userId, onClose, onCreated }: CreateEventPanelProps) {
  const now = new Date();
  const defaultStart = new Date(now.getTime() + 60 * 60 * 1000);
  const defaultEnd = new Date(defaultStart.getTime() + 3 * 60 * 60 * 1000);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [startAt, setStartAt] = useState(toLocalInput(defaultStart));
  const [endAt, setEndAt] = useState(toLocalInput(defaultEnd));
  const [isPublic, setIsPublic] = useState(false);
  const [allowGuestDownloads, setAllowGuestDownloads] = useState(true);
  const [joinType, setJoinType] = useState<CreateWebEventInput['joinType']>('code');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await createWebEvent(
        {
          name,
          description,
          locationName,
          locationAddress,
          startTimeIso: new Date(startAt).toISOString(),
          endTimeIso: new Date(endAt).toISOString(),
          isPublic,
          allowGuestDownloads,
          joinType,
        },
        userId,
      );
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create this event.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="portal-modal" role="dialog" aria-modal="true" aria-label="Create a Gathrd event">
      <div className="portal-modal-card create-event-modal">
        <div className="portal-modal-heading">
          <div>
            <p className="eyebrow">Create</p>
            <h2>Start a shared event.</h2>
            <p>Set the essentials here. Native Auto-Gather and location geofencing remain mobile-only.</p>
          </div>
          <button className="portal-close" type="button" onClick={onClose} aria-label="Close create event">×</button>
        </div>

        <form className="portal-form" onSubmit={handleSubmit}>
          <label>
            Event name
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Sarah's Baby Shower" required />
          </label>
          <label>
            Description
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="A brief description for your guests" rows={3} />
          </label>
          <div className="portal-form-grid two-up">
            <label>
              Location name
              <input value={locationName} onChange={(event) => setLocationName(event.target.value)} placeholder="The Cotton Room" />
            </label>
            <label>
              Address
              <input value={locationAddress} onChange={(event) => setLocationAddress(event.target.value)} placeholder="123 Main Street" />
            </label>
          </div>
          <div className="portal-form-grid two-up">
            <label>
              Start
              <input type="datetime-local" value={startAt} onChange={(event) => setStartAt(event.target.value)} required />
            </label>
            <label>
              End
              <input type="datetime-local" value={endAt} onChange={(event) => setEndAt(event.target.value)} required />
            </label>
          </div>

          <div className="portal-choice-card">
            <label className="portal-check-row">
              <input type="checkbox" checked={isPublic} onChange={(event) => setIsPublic(event.target.checked)} />
              <span><strong>Public event</strong><small>Allow the event to participate in public/discovery experiences.</small></span>
            </label>
            <label className="portal-check-row">
              <input type="checkbox" checked={allowGuestDownloads} onChange={(event) => setAllowGuestDownloads(event.target.checked)} />
              <span><strong>Guest downloads</strong><small>Guests may save event photos when their access allows it.</small></span>
            </label>
          </div>

          {isPublic && (
            <label>
              Join method
              <select value={joinType} onChange={(event) => setJoinType(event.target.value as CreateWebEventInput['joinType'])}>
                <option value="open">Open</option>
                <option value="code">Code</option>
                <option value="verified">Host approval</option>
              </select>
            </label>
          )}

          {error && <p className="error-banner portal-inline-error">{error}</p>}
          <div className="portal-form-actions">
            <button className="secondary-button" type="button" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="primary-button" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create event'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
